import { useMemo } from 'react';
import {
  buildChildrenByValue,
  buildDescendantsByValue,
  buildParentByValue,
  buildSelectableByValue,
  flattenItemsForRender,
} from '../utils/tree';
import { type NestedGroupItem, type DropdownValue, type FlatItem } from '../types';

type UseDropdownTreeDataResult = {
  allItems: FlatItem[];
  itemsByDepthDesc: FlatItem[];
  labelByValue: Map<DropdownValue, string>;
  selectableByValue: Map<DropdownValue, boolean>;
  descendantsByValue: Map<DropdownValue, DropdownValue[]>;
  childrenByValue: Map<DropdownValue, DropdownValue[]>;
  parentByValue: Map<DropdownValue, DropdownValue | null>;
  allValues: DropdownValue[];
  expandableValues: DropdownValue[];
};

export function useDropdownTreeData(items: NestedGroupItem[]): UseDropdownTreeDataResult {
  // Flatten the tree and validate uniqueness of values.
  const allItems = useMemo(() => {
    const flattened = flattenItemsForRender(items);
    const seenValues = new Set<DropdownValue>();

    for (const item of flattened) {
      if (seenValues.has(item.value)) {
        throw new Error(`DropdownV2 items contain duplicate value: "${item.value}"`);
      }

      seenValues.add(item.value);
    }

    return flattened;
  }, [items]);

  // Precompute data structures for selection logic and rendering.
  const itemsByDepthDesc = useMemo(
    () => [...allItems].sort((a, b) => b.depth - a.depth),
    [allItems],
  );

  // Labels shown in the trigger.
  const labelByValue = useMemo(
    () => new Map(allItems.map((item) => [item.value, item.selectedLabel ?? item.label])),
    [allItems],
  );

  // Build maps for quick lookup of selectable status, descendants, children, and parent relationships.
  const { selectableByValue, descendantsByValue, childrenByValue, parentByValue } = useMemo(() => {
    const selectable = buildSelectableByValue(items);

    return {
      selectableByValue: selectable,
      descendantsByValue: buildDescendantsByValue(items, selectable),
      childrenByValue: buildChildrenByValue(items),
      parentByValue: buildParentByValue(items),
    };
  }, [items]);

  // Extract all selectable values for easy access.
  const allValues = useMemo(
    () => allItems.filter((item) => selectableByValue.get(item.value)).map((item) => item.value),
    [allItems, selectableByValue],
  );

  // Identify which items are expandable, regardless of whether they are groups or values.
  const expandableValues = useMemo(
    () =>
      allItems
        .filter((item) => (childrenByValue.get(item.value) ?? []).length > 0)
        .map((item) => item.value),
    [allItems, childrenByValue],
  );

  return {
    allItems,
    itemsByDepthDesc,
    labelByValue,
    selectableByValue,
    descendantsByValue,
    childrenByValue,
    parentByValue,
    allValues,
    expandableValues,
  };
}
